import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Home, Phone } from 'lucide-react';
import { FaqSection } from '../components/SEO';
import { ORGANIZATION_LD } from '../components/SEO';
import { PageHero } from '../components/PageShell';
import { PageShell } from '../components/PageShell';
import { SEO } from '../components/SEO';
import { SITE_URL } from '../lib/constants';
import { buildFaqLd } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const SportsBarPage = () => {
  const { t, language } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const photos = [
    'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/vqamn5j5_DSC07851.jpg',
    'https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/ns9ier8z_DSC06847.webp',
    'https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/nj8h18ke_DSC06837.webp'
  ];
  const seo = t.seo.sportsBar;
  const faqs = t.sportsBarPage.faqs || [];
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BarOrPub',
      '@id': `${SITE_URL}/sports-bar#bar`,
      name: 'Sports Bar Padelarena Wädenswil',
      url: `${SITE_URL}/sports-bar`,
      image: photos.map(p => p),
      description: seo.description,
      address: ORGANIZATION_LD.address,
      telephone: '+41 43 833 0834',
      servesCuisine: ['Pizza', 'Flammkuchen', 'Chnoblibrot', 'Snacks', 'Beer', 'Wine'],
      amenityFeature: [
        { '@type': 'LocationFeatureSpecification', name: 'TVs', value: 9 },
        { '@type': 'LocationFeatureSpecification', name: 'Live Sports' },
        { '@type': 'LocationFeatureSpecification', name: 'Lounge area' },
        { '@type': 'LocationFeatureSpecification', name: 'Free Wi-Fi' }
      ],
      parentOrganization: { '@id': `${SITE_URL}/#organization` }
    },
    faqs.length ? buildFaqLd(faqs) : null
  ].filter(Boolean);

  return (
    <PageShell testId="sportsbar-page">
      <SEO
        path="/sports-bar"
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        image={photos[0]}
        jsonLd={jsonLd}
      />
      <PageHero
        label={t.sportsBarPage.label}
        title={t.sportsBarPage.titleStart}
        accent={t.sportsBarPage.titleAccent}
        subtitle={t.sportsBarPage.subtitle}
        image={photos[0]}
      />
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-12">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t.sportsBarPage.aboutTitle}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">{t.sportsBarPage.about1}</p>
            <p className="text-slate-700 leading-relaxed">{t.sportsBarPage.about2}</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: '9', l: t.sportsBarPage.statTvs },
              { v: '7d/7', l: t.sportsBarPage.statOpen },
              { v: 'LIVE', l: t.sportsBarPage.statLive },
              { v: '∞', l: t.sportsBarPage.statVibes }
            ].map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-4 text-center border border-slate-100">
                <p className="text-3xl font-black text-slate-900">{s.v}</p>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.l}</p>
              </div>
            ))}
          </div>

          {/* Photo strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {photos.map((src, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-100">
                <img src={src} alt={`Sports Bar ${i+1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.sportsBarPage.liveTitle}</h3>
            <div className="flex flex-wrap gap-2">
              {t.sportsBarPage.liveSports.map((sp, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold border border-blue-100">
                  {sp}
                </span>
              ))}
            </div>
          </div>

          <div data-testid="sportsbar-food-section">
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.sportsBarPage.foodTitle}</h3>
            <div className="flex flex-wrap gap-2">
              {t.sportsBarPage.foodItems.map((it, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 text-sm font-semibold border border-amber-100">
                  {it}
                </span>
              ))}
            </div>
          </div>

          <div data-testid="sportsbar-featured-event">
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.sportsBarPage.featuredEventTitle}</h3>
            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center">
              <p className="text-slate-700 font-semibold mb-1">{t.eventsPage.pausedTitle}</p>
              <p className="text-slate-500 text-sm">{t.eventsPage.pausedNote}</p>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.sportsBarPage.featuresTitle}</h3>
            <ul className="space-y-2 text-slate-700">
              {t.sportsBarPage.features.map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-slate-900 text-white rounded-3xl p-8 md:p-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">{t.sportsBarPage.ctaTitle}</h3>
            <p className="text-slate-300 mb-6">{t.sportsBarPage.ctaSubtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:+41438330834" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-slate-900 hover:bg-slate-100 transition-colors">
                <Phone className="w-4 h-4" /> +41 43 833 0834
              </a>
              <Link to="/events" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                {t.sportsBarPage.privateBtn}
              </Link>
            </div>
          </div>

          {faqs.length > 0 && (
            <FaqSection title={t.sportsBarPage.faqTitle} faqs={faqs} />
          )}
        </div>
      </section>
    </PageShell>
  );
};

// Home Page Component
// AI Chat Widget — floating bubble bottom-right, powered by Claude Sonnet 5

export default SportsBarPage;
